/**
 * 🌐 Backend API Client - 프론트엔드용 백엔드 API 클라이언트
 *
 * 🎯 핵심 철학:
 * - 프론트엔드는 백엔드 API만 사용
 * - 직접 데이터베이스 접근 금지
 * - 타입 안전성 유지
 * - 에러 처리 통합
 *
 * ✅ 올바른 구조:
 * 프론트엔드 → Backend API → Supabase Repository → PostgreSQL
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type { EvidenceRef } from '@shared/flow/process-layer';
import type {
  ExecutorLedgerItem,
  WorkspaceInsightsSummary,
  WorkspaceMyTaskDetail,
  WorkspaceMyTaskItem,
  WorkspaceOverview,
  WorkspaceOperationDetail,
} from '@shared/types/workspace';
import type { DomainFieldSchema } from '@shared/types';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { FinanceDashboardTimeSeriesResponse } from '@shared/types/finance-dashboard';
import type {
  FinanceOverdueListResult,
  FinanceReceivablesListResult,
} from '@domain/finance/finance-receivables-query';
import { resolveDomainApiListPath } from '@shared/domain/resolve-domain-api-list-path';
import { getCollectionNameFromBusinessDomain } from '../utils/businessDomainUtils';
import type { ApiResponse, PaginatedResponse } from '@shared/types/api-common';
import type { WorkbenchException } from '@shared/business-workbench/exception-registry';
import { COLLECTION_CONTACT_FLOW_OPERATOR_LABELS } from '@shared/business-workbench/collection-contact-flow-operator-labels';
import { COLLECTION_COMMITMENT_OPERATOR_LABELS } from '@shared/business-workbench/collection-commitment-operator-labels';
import { COLLECTION_CASE_SUMMARY_OPERATOR_LABELS } from '@shared/business-workbench/collection-case-summary-operator-labels';
import { COLLECTION_CASE_TIMELINE_OPERATOR_LABELS } from '@shared/business-workbench/collection-case-timeline-operator-labels';
import { COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS } from '@shared/business-workbench/collection-case-read-links-operator-labels';
import { COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS } from '@shared/business-workbench/collection-stage-recommendation-operator-labels';
import type { CollectionCommitment } from '@shared/collection/collection-commitment-types';
import type { CollectionCaseSummary } from '@shared/collection/collection-case-summary-types';
import type { CollectionCaseTimeline } from '@shared/collection/collection-case-timeline-types';
import type { CollectionCaseReadLinks } from '@shared/collection/collection-case-read-links-types';
import type {
  CollectionStageRecommendationHistoryPage,
  CollectionStageRecommendationProjection,
} from '@shared/collection/collection-stage-recommendation-projection-types';
import type { CollectionManagerExceptionQueuePageDto } from '@shared/collection/collection-manager-exception-queue-projection-types';
import type { CollectionDashboardSummaryDto } from '@shared/collection/collection-dashboard-summary-types';
import { COLLECTION_DASHBOARD_OPERATOR_LABELS } from '@shared/business-workbench/collection-dashboard-operator-labels';
import type { CollectionOperatorProjectionResponseV0 } from '@shared/projection/CollectionOperatorProjectionV0';
import { COLLECTION_OPERATOR_PROJECTION_LABELS } from '@shared/business-workbench/collection-operator-projection-labels';
import type { CollectionManagerReviewDispositionMutationResponseDto } from '@shared/collection/collection-manager-review-disposition-api-types';
import type {
  CollectionCreditHoldDismissResponseDto,
  CollectionCreditHoldDispositionMutationResponseDto,
} from '@shared/collection/collection-credit-hold-recommendation-api-types';
import type { CollectionCreditHoldRecommendationQueuePage } from '@shared/collection/collection-credit-hold-recommendation-projection-types';
import type {
  DocumentCorrectionOpsEligibleQueuePageDto,
  DocumentCorrectionOpsOpenInfoRequestResponseDto,
} from '@shared/portal/document-correction/document-correction-ops-info-request-api-types';
import type {
  DocumentCorrectionOpsReviewContextDto,
  DocumentCorrectionOpsReviewDecisionResponseDto,
} from '@shared/portal/document-correction/document-correction-ops-review-decision-api-types';
import type {
  DocumentCorrectionReviewDecisionReasonCode,
  DocumentCorrectionReviewDecisionWire,
} from '@shared/portal/document-correction/document-correction-review-decision-v0-types';
import { DOCUMENT_CORRECTION_OPS_INFO_REQUEST_OPERATOR_LABELS } from '@shared/business-workbench/document-correction-ops-info-request-operator-labels';
import { DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_OPERATOR_LABELS } from '@shared/business-workbench/document-correction-ops-review-decision-operator-labels';
import type {
  CreditHoldQueueCategory,
  CreditHoldQueuePage,
} from '@shared/collection/credit-hold-queue-projection-types';
import type {
  FinalRequestDocumentQueueCategory,
  FinalRequestDocumentQueuePage,
} from '@shared/collection/final-request-document-queue-projection-types';
import type {
  LegalActionRequestQueueCategory,
  LegalActionRequestQueuePage,
} from '@shared/collection/legal-action-request-queue-projection-types';
import type {
  CollectionSettlementRequestQueueCategory,
  CollectionSettlementRequestQueuePage,
} from '@shared/collection/collection-settlement-request-queue-projection-types';
import type {
  AccountingPostingRequestQueueCategory,
  AccountingPostingRequestQueuePage,
} from '@shared/accounting-integration/accounting-posting-request-queue-projection-types';
import type {
  OrderGateDenialLogPage,
  OrderGateOverrideListPage,
  OrderGateOverrideListScope,
} from '@shared/collection/order-gate-read-projection-types';
import type {
  CollectionFinalRequestPrepDismissResponseDto,
  CollectionFinalRequestPrepDispositionMutationResponseDto,
} from '@shared/collection/collection-final-request-prep-recommendation-api-types';
import type {
  CollectionFinalRequestPrepRecommendationActivePage,
  CollectionFinalRequestPrepRecommendationQueuePage,
} from '@shared/collection/collection-final-request-prep-recommendation-projection-types';
import { COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS } from '@shared/business-workbench/collection-manager-exception-queue-operator-labels';
import { COLLECTION_MANAGER_REVIEW_OPERATOR_LABELS } from '@shared/business-workbench/collection-manager-review-operator-labels';
import { COLLECTION_CREDIT_HOLD_OPERATOR_LABELS } from '@shared/business-workbench/collection-credit-hold-operator-labels';
import { COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS } from '@shared/business-workbench/collection-final-request-prep-operator-labels';
import type {
  CollectionCreditReviewRequestDto,
  CollectionCreditReviewRequestQueuePage,
} from '@shared/collection/collection-credit-review-request-api-types';
import { COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS } from '@shared/business-workbench/collection-credit-review-operator-labels';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';

/** BE PaginationSchema.max — FE list 호출 시 이 값 초과 전송을 방어 클램프로 차단 */
const BE_LIST_LIMIT_MAX = 100;
import { logger } from '../../../../shared/logger';
import { getAuthHeaders } from '../utils/jwtUtils';
import { fetchWithAuth } from './authFetch';
import type {
  ApiErrorBody,
  ExportEstimateResult,
  ExportJobView,
  ExportSubdomainInfo,
  ExportSubdomainsResponse,
  FlowTwoFactorSendResult,
  GlobalSearchItem,
  GlobalSearchResult,
  RelationNetworkNode,
  V2FlowDefinitionSummary,
  V2FlowExecutionSummary,
} from './backendApiClient.types';
import { appendFinanceListQueryParams, mapApiErrorBody } from './backendApiClient.httpHelpers';

export type {
  ApiError,
  ApiErrorBody,
  ApiRequestError,
  ExportEstimateResult,
  ExportJobStatus,
  ExportJobView,
  ExportSubdomainInfo,
  FlowTwoFactorSendResult,
  GlobalSearchItem,
  GlobalSearchResult,
  RelationNetworkNode,
  RelationNetworkRelation,
  V2FlowDefinitionSummary,
  V2FlowExecutionSummary,
  V2FlowManualRecoveryOperationSummary,
  V2FlowManualRecoverySummary,
} from './backendApiClient.types';

// ============================================
// 🎯 타입 정의 (Paginated alias kept local)
// ============================================

// ✅ 표준화: 백엔드 표준 ApiResponse 사용 (위에서 import)
// ApiListResponse는 PaginatedResponse로 통일
type ApiListResponse<T = unknown> = PaginatedResponse<T>;

// ============================================
// 🎯 Backend API 클라이언트
// ============================================

export class BackendApiClient {
  private baseUrl: string;
  private tenantId: string;

  constructor(tenantId: string, baseUrl?: string) {
    this.tenantId = tenantId;
    // 환경 변수 또는 기본값 사용
    this.baseUrl = baseUrl ?? resolveApiOrigin();
  }

  // ============================================
  // 🔧 유틸리티 메서드
  // ============================================

  /**
   * BusinessDomain을 API 경로로 변환
   * 예: 'internal_entity' → '/api/domain/entity/internal/internal' (Universal CRUD list mount)
   */
  private getApiPath(businessDomain: string): string {
    const resolved = resolveDomainApiListPath(businessDomain);
    if (resolved) {
      return resolved;
    }

    // 기본 경로 (collectionName 그대로 사용)
    const collectionName = getCollectionNameFromBusinessDomain(businessDomain);
    return `/api/domain/${collectionName}`;
  }

  /**
   * HTTP 요청 실행
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const { signal: requestSignal, ...restOptions } = options;

    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-tenant-id': this.tenantId,
        ...getAuthHeaders(),
        ...restOptions.headers,
      },
      ...restOptions,
      signal: requestSignal ?? AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    };

    try {
      logger.debug(`🌐 API Request: ${options.method || 'GET'} ${url}`);

      const response = await fetchWithAuth(url, defaultOptions);

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: ApiErrorBody;
        try {
          errorData = JSON.parse(errorText) as ApiErrorBody;
        } catch {
          errorData = { message: errorText };
        }

        throw mapApiErrorBody(errorData, response.status);
      }

      const data = await response.json();
      logger.debug(`✅ API Response: ${url}`, { data: data });

      return data;
    } catch (error) {
      logger.error(`❌ API Request Error: ${url}`, error instanceof Error ? error : undefined, {
        raw: error,
      });
      throw error;
    }
  }

  // ============================================
  // 📊 CRUD 메서드
  // ============================================

  /**
   * 목록 조회
   */
  async list<T extends UniversalBaseEntity>(
    businessDomain: string,
    filters?: Record<string, string | number | boolean>
  ): Promise<T[]> {
    const path = this.getApiPath(businessDomain);
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const safeValue =
            key === 'limit' && typeof value === 'number'
              ? Math.min(value, BE_LIST_LIMIT_MAX)
              : value;
          queryParams.append(key, String(safeValue));
        }
      });
    }

    const url = queryParams.toString() ? `${path}?${queryParams.toString()}` : path;

    const response = await this.request<ApiListResponse<T>>(url);
    return response.data || [];
  }

  /**
   * Finance dashboard — 월별 수익·비용·잔액·예산 시계열
   */
  async getFinanceDashboardTimeSeries(options?: {
    months?: number;
  }): Promise<FinanceDashboardTimeSeriesResponse> {
    const params = new URLSearchParams();
    if (options?.months !== undefined) {
      params.set('months', String(options.months));
    }
    const qs = params.toString();
    const response = await this.request<{
      success: boolean;
      data?: FinanceDashboardTimeSeriesResponse;
    }>(`/api/domain/finance/dashboard/time-series${qs ? `?${qs}` : ''}`);

    if (response.data) {
      return response.data;
    }

    return {
      buckets: [],
      currency: 'KRW',
      hasAmounts: false,
      months: options?.months ?? 12,
    };
  }

  /**
   * Finance receivables — 미수금(AR) 전용 목록 + aging bucket 집계
   */
  async getFinanceReceivables(
    queryParams?: Record<string, string | number | boolean>
  ): Promise<FinanceReceivablesListResult> {
    const response = await this.request<{
      success: boolean;
      data?: FinanceReceivablesListResult;
    }>(appendFinanceListQueryParams('/api/domain/finance/receivables', queryParams));

    if (response.data) {
      return response.data;
    }

    return {
      items: [],
      agingSummary: {
        current: 0,
        '1-30': 0,
        '31-60': 0,
        '61-90': 0,
        '90+': 0,
      },
      total: 0,
    };
  }

  /**
   * Finance overdue — 연체 미수금 전용 목록
   */
  async getFinanceOverdue(
    queryParams?: Record<string, string | number | boolean>
  ): Promise<FinanceOverdueListResult> {
    const response = await this.request<{
      success: boolean;
      data?: FinanceOverdueListResult;
    }>(appendFinanceListQueryParams('/api/domain/finance/overdue', queryParams));

    if (response.data) {
      return response.data;
    }

    return {
      items: [],
      total: 0,
    };
  }

  /**
   * 단일 항목 조회
   */
  async getById<T extends UniversalBaseEntity>(
    businessDomain: string,
    id: string
  ): Promise<T | null> {
    const path = this.getApiPath(businessDomain);
    const response = await this.request<ApiResponse<T>>(`${path}/${id}`);
    return response.data || null;
  }

  /**
   * 생성
   */
  async create<T extends UniversalBaseEntity>(
    businessDomain: string,
    data: Partial<T>
  ): Promise<T> {
    const path = this.getApiPath(businessDomain);
    const response = await this.request<ApiResponse<T>>(path, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.data) {
      throw new Error('Create failed: No data returned');
    }

    return response.data;
  }

  /**
   * 업데이트
   */
  async update<T extends UniversalBaseEntity>(
    businessDomain: string,
    id: string,
    data: Partial<T>
  ): Promise<T> {
    const path = this.getApiPath(businessDomain);
    const response = await this.request<ApiResponse<T>>(`${path}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.data) {
      throw new Error('Update failed: No data returned');
    }

    return response.data;
  }

  /**
   * 삭제
   */
  async delete(businessDomain: string, id: string): Promise<void> {
    const path = this.getApiPath(businessDomain);
    await this.request(`${path}/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 스키마 조회
   * 백엔드의 스키마 API 엔드포인트 사용: /api/system/schema/:subDomain
   */
  async getSchema(businessDomain: string): Promise<DomainFieldSchema> {
    const response = await this.request<ApiResponse<DomainFieldSchema>>(
      `/api/system/schema/${businessDomain}`
    );
    if (!response.data) {
      throw new Error(`Schema not found for ${businessDomain}`);
    }
    return response.data;
  }

  /**
   * 스키마 목록 조회
   */
  async getSchemaList(domain?: string): Promise<DomainFieldSchema[]> {
    const queryParams = domain ? `?domain=${domain}` : '';
    const response = await this.request<ApiListResponse<DomainFieldSchema>>(
      `/api/system/schema/list${queryParams}`
    );
    return response.data || [];
  }

  /**
   * 전역 검색 (Unified Search Integration API — Wave 2a)
   * GET /api/integration/search?q=...&limit=...
   */
  /**
   * 통합 대시보드 (Wave 2b)
   * GET /api/integration/dashboard?days=&includeAI=&domains=
   */
  /**
   * Workspace overview (Objects · Encoder · Executor)
   * GET /api/workspace/overview?entityId=...
   */
  async getWorkspaceOverview(entityId: string): Promise<WorkspaceOverview> {
    const params = new URLSearchParams({ entityId });
    const response = await this.request<ApiResponse<WorkspaceOverview>>(
      `/api/workspace/overview?${params.toString()}`
    );
    if (!response.data) {
      throw new Error('Workspace overview를 불러오지 못했습니다.');
    }
    return response.data;
  }

  /**
   * Workspace Operations Ledger (completed Dynamic executions)
   * GET /api/workspace/executor-ledger?entityId=...
   */
  async getWorkspaceExecutorLedger(
    entityId: string,
    options?: { limit?: number }
  ): Promise<ExecutorLedgerItem[]> {
    const params = new URLSearchParams({ entityId });
    if (options?.limit !== undefined) {
      params.set('limit', String(options.limit));
    }
    const response = await this.request<ApiResponse<ExecutorLedgerItem[]>>(
      `/api/workspace/executor-ledger?${params.toString()}`
    );
    return response.data ?? [];
  }

  /**
   * Workspace Insights summary (conscious/judgment cards)
   * GET /api/workspace/insights-summary?entityId=...
   */
  async getWorkspaceInsightsSummary(
    entityId: string,
    options?: { limit?: number }
  ): Promise<WorkspaceInsightsSummary> {
    const params = new URLSearchParams({ entityId });
    if (options?.limit !== undefined) {
      params.set('limit', String(options.limit));
    }
    const response = await this.request<ApiResponse<WorkspaceInsightsSummary>>(
      `/api/workspace/insights-summary?${params.toString()}`
    );
    if (!response.data) {
      throw new Error('Workspace insights를 불러오지 못했습니다.');
    }
    return response.data;
  }

  /**
   * Workspace Operation detail
   * GET /api/workspace/operations/:operationId
   */
  async getWorkspaceOperation(operationId: string): Promise<WorkspaceOperationDetail> {
    const response = await this.request<ApiResponse<WorkspaceOperationDetail>>(
      `/api/workspace/operations/${encodeURIComponent(operationId)}`
    );
    if (!response.data) {
      throw new Error('Operation 실행 정보를 불러오지 못했습니다.');
    }
    return response.data;
  }

  /**
   * Workspace Operation complete (pending → executed)
   * POST /api/workspace/operations/:operationId/complete
   */
  async completeWorkspaceOperation(
    operationId: string,
    payload?: {
      action?: 'approve' | 'confirm' | 'execute';
      evidence?: Record<string, unknown>;
      twoFactorCode?: string;
    }
  ): Promise<WorkspaceOperationDetail> {
    const response = await this.request<ApiResponse<WorkspaceOperationDetail>>(
      `/api/workspace/operations/${encodeURIComponent(operationId)}/complete`,
      {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }
    );
    if (!response.data) {
      throw new Error('Operation 완료 처리에 실패했습니다.');
    }
    return response.data;
  }

  /**
   * Workspace My Tasks — TaskExecution queue (v2.5 P3)
   * GET /api/workspace/my-tasks?entityId=...
   */
  async getWorkspaceMyTasks(entityId: string): Promise<WorkspaceMyTaskItem[]> {
    const params = new URLSearchParams({ entityId });
    const response = await this.request<ApiResponse<WorkspaceMyTaskItem[]>>(
      `/api/workspace/my-tasks?${params.toString()}`
    );
    return response.data ?? [];
  }

  /**
   * Workbench exceptions for entity navigator (WB3-4)
   * GET /api/workspace/exceptions?entityId=...
   */
  async getWorkbenchExceptions(entityId: string): Promise<WorkbenchException[]> {
    const params = new URLSearchParams({ entityId });
    const response = await this.request<ApiResponse<WorkbenchException[]>>(
      `/api/workspace/exceptions?${params.toString()}`
    );
    return response.data ?? [];
  }

  /**
   * Workspace Task detail
   * GET /api/workspace/tasks/:taskExecutionId?flowExecutionId=...
   */
  async getWorkspaceTaskDetail(
    taskExecutionId: string,
    flowExecutionId: string
  ): Promise<WorkspaceMyTaskDetail> {
    const params = new URLSearchParams({ flowExecutionId });
    const response = await this.request<ApiResponse<WorkspaceMyTaskDetail>>(
      `/api/workspace/tasks/${encodeURIComponent(taskExecutionId)}?${params.toString()}`
    );
    if (!response.data) {
      throw new Error('할 일 상세를 불러오지 못했습니다.');
    }
    return response.data;
  }

  /**
   * Complete TaskExecution (approve / reject / complete)
   * POST /api/workspace/tasks/:taskExecutionId/complete
   */
  async completeWorkspaceTask(
    taskExecutionId: string,
    payload: {
      flowExecutionId: string;
      decision?: { outcome: 'approved' | 'rejected' | 'completed' | 'skipped'; reason?: string };
      evidence?: Array<{
        id: string;
        type: string;
        label?: string;
        attachmentId?: string;
        attachedAt?: string;
        attachedBy?: string;
      }>;
      partialDelivery?: { batchQuantity: number };
    }
  ): Promise<{ task: { id: string; status: string } }> {
    const response = await this.request<
      ApiResponse<{ task: { id: string; status: string; flowExecutionId: string } }>
    >(`/api/workspace/tasks/${encodeURIComponent(taskExecutionId)}/complete`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error('할 일 처리에 실패했습니다.');
    }
    return response.data;
  }

  /**
   * Assign delivery partner on dispatch-stage task (Phase 3 S1 — metadata only).
   * POST /api/workspace/tasks/:taskExecutionId/dispatch-delivery-partner
   */
  async assignDispatchDeliveryPartner(
    taskExecutionId: string,
    payload: {
      flowExecutionId: string;
      deliveryPartnerExternalEntityId: string;
      note?: string;
    }
  ): Promise<{
    assignment: {
      deliveryPartnerExternalEntityId: string;
      assignedAt: string;
      assignedBy: string;
      assignmentRevision: number;
      note?: string;
    };
    idempotent: boolean;
    taskDetail?: WorkspaceMyTaskDetail;
  }> {
    const response = await this.request<
      ApiResponse<{
        assignment: {
          deliveryPartnerExternalEntityId: string;
          assignedAt: string;
          assignedBy: string;
          assignmentRevision: number;
          note?: string;
        };
        idempotent: boolean;
        taskDetail?: WorkspaceMyTaskDetail;
      }>
    >(`/api/workspace/tasks/${encodeURIComponent(taskExecutionId)}/dispatch-delivery-partner`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error('배송 파트너 지정에 실패했습니다.');
    }
    return response.data;
  }

  /**
   * Attach uploaded ObjectAttachment to TaskExecution evidence (pre-complete persist).
   * POST /api/workspace/tasks/:taskExecutionId/evidence/:evidenceRefId/attach
   */
  async attachWorkspaceTaskEvidence(
    taskExecutionId: string,
    evidenceRefId: string,
    payload: {
      flowExecutionId: string;
      attachmentId: string;
      type?: string;
      label?: string;
    }
  ): Promise<{ evidence: EvidenceRef[] }> {
    const response = await this.request<ApiResponse<{ evidence: EvidenceRef[] }>>(
      `/api/workspace/tasks/${encodeURIComponent(taskExecutionId)}/evidence/${encodeURIComponent(evidenceRefId)}/attach`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error('증빙 첨부에 실패했습니다.');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/contact-flow
   */
  async openArCollectionContactFlow(
    arEntryId: string,
    payload?: { actorEntityId?: string }
  ): Promise<{
    arEntryId: string;
    flowExecutionId: string;
    flowDefinitionId: string;
    taskExecutionId: string | null;
    status: string;
    actionable: boolean;
    resumedExistingFlow: boolean;
  }> {
    const response = await this.request<
      ApiResponse<{
        arEntryId: string;
        flowExecutionId: string;
        flowDefinitionId: string;
        taskExecutionId: string | null;
        status: string;
        actionable: boolean;
        resumedExistingFlow: boolean;
      }>
    >(`/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/contact-flow`, {
      method: 'POST',
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    if (!response.data) {
      throw new Error(COLLECTION_CONTACT_FLOW_OPERATOR_LABELS.openFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/summary
   */
  async getArCollectionCaseSummary(arEntryId: string): Promise<CollectionCaseSummary> {
    const response = await this.request<ApiResponse<CollectionCaseSummary>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/summary`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CASE_SUMMARY_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/timeline
   */
  async getArCollectionCaseTimeline(arEntryId: string): Promise<CollectionCaseTimeline> {
    const response = await this.request<ApiResponse<CollectionCaseTimeline>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/timeline`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CASE_TIMELINE_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/read-links
   */
  async getArCollectionCaseReadLinks(arEntryId: string): Promise<CollectionCaseReadLinks> {
    const response = await this.request<ApiResponse<CollectionCaseReadLinks>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/read-links`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CASE_READ_LINKS_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/recommendations/active
   */
  async getArCollectionRecommendationActive(
    arEntryId: string
  ): Promise<CollectionStageRecommendationProjection | null> {
    const response = await this.request<
      ApiResponse<CollectionStageRecommendationProjection | null>
    >(`/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/recommendations/active`, {
      method: 'GET',
    });
    return response.data ?? null;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/recommendations/history
   */
  async listArCollectionRecommendationHistory(
    arEntryId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<CollectionStageRecommendationHistoryPage> {
    const path = appendFinanceListQueryParams(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/recommendations/history`,
      params
    );
    const response = await this.request<ApiResponse<CollectionStageRecommendationHistoryPage>>(
      path,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/manager-exception-queue
   */
  async listManagerExceptionQueue(params?: {
    flags?: string[];
    severity?: string;
    overdueBucket?: string;
    search?: string;
    limit?: number;
    cursor?: string;
    includeDeferred?: boolean;
  }): Promise<CollectionManagerExceptionQueuePageDto> {
    const path = appendFinanceListQueryParams(
      '/api/workspace/ar-collections/manager-exception-queue',
      {
        ...(params?.flags && params.flags.length > 0 ? { flag: params.flags.join(',') } : {}),
        ...(params?.severity ? { severity: params.severity } : {}),
        ...(params?.overdueBucket ? { overdueBucket: params.overdueBucket } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.cursor ? { cursor: params.cursor } : {}),
        ...(params?.includeDeferred ? { includeDeferred: 'true' } : {}),
      }
    );
    const response = await this.request<ApiResponse<CollectionManagerExceptionQueuePageDto>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error(COLLECTION_MANAGER_EXCEPTION_QUEUE_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/collection/dashboard/summary
   */
  async getCollectionDashboardSummary(params?: {
    asOf?: string;
    includeDeferred?: boolean;
  }): Promise<CollectionDashboardSummaryDto> {
    const path = appendFinanceListQueryParams('/api/workspace/collection/dashboard/summary', {
      ...(params?.asOf ? { asOf: params.asOf } : {}),
      ...(params?.includeDeferred ? { includeDeferred: 'true' } : {}),
    });
    const response = await this.request<ApiResponse<CollectionDashboardSummaryDto>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error(COLLECTION_DASHBOARD_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/collection/operator-projection/:arEntryId
   * Family B sealed read-model — Family C read-only consumer. No mutation.
   */
  async getCollectionOperatorProjection(
    arEntryId: string,
    params?: {
      audienceProfileId?: string;
      projectionProfileId?: string;
    }
  ): Promise<CollectionOperatorProjectionResponseV0> {
    const path = appendFinanceListQueryParams(
      `/api/workspace/collection/operator-projection/${encodeURIComponent(arEntryId)}`,
      {
        ...(params?.audienceProfileId ? { audienceProfileId: params.audienceProfileId } : {}),
        ...(params?.projectionProfileId ? { projectionProfileId: params.projectionProfileId } : {}),
      }
    );
    const response = await this.request<ApiResponse<CollectionOperatorProjectionResponseV0>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error(COLLECTION_OPERATOR_PROJECTION_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/manager-review-dispositions/acknowledge
   */
  async acknowledgeManagerReviewDisposition(payload: {
    collectionCaseId: string;
    exceptionCode: string;
    sourceFingerprint: string;
    clientIdempotencyKey: string;
    notes?: string | null;
  }): Promise<CollectionManagerReviewDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionManagerReviewDispositionMutationResponseDto>
    >('/api/workspace/ar-collections/manager-review-dispositions/acknowledge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error(COLLECTION_MANAGER_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/manager-review-dispositions/defer
   */
  async deferManagerReviewDisposition(payload: {
    collectionCaseId: string;
    exceptionCode: string;
    sourceFingerprint: string;
    clientIdempotencyKey: string;
    reasonCode: string;
    expiresAt: string;
    notes?: string | null;
  }): Promise<CollectionManagerReviewDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionManagerReviewDispositionMutationResponseDto>
    >('/api/workspace/ar-collections/manager-review-dispositions/defer', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error(COLLECTION_MANAGER_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/collection/credit-hold-recommendations
   */
  async listCreditHoldRecommendations(params?: {
    limit?: number;
    offset?: number;
    includeDeferred?: boolean;
  }): Promise<CollectionCreditHoldRecommendationQueuePage> {
    const path = appendFinanceListQueryParams(
      '/api/workspace/collection/credit-hold-recommendations',
      {
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.offset !== undefined ? { offset: params.offset } : {}),
        ...(params?.includeDeferred ? { includeDeferred: 'true' } : {}),
      }
    );
    const response = await this.request<ApiResponse<CollectionCreditHoldRecommendationQueuePage>>(
      path,
      {
        method: 'GET',
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/portal/document-correction-requests
   * Eligible under_review document_correction without open info_requested.
   */
  async listDocumentCorrectionOpsEligibleQueue(params?: {
    limit?: number;
    offset?: number;
  }): Promise<DocumentCorrectionOpsEligibleQueuePageDto> {
    const path = appendFinanceListQueryParams(
      '/api/workspace/portal/document-correction-requests',
      {
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.offset !== undefined ? { offset: params.offset } : {}),
      }
    );
    const response = await this.request<ApiResponse<DocumentCorrectionOpsEligibleQueuePageDto>>(
      path,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(DOCUMENT_CORRECTION_OPS_INFO_REQUEST_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/portal/document-correction-requests/:requestId/info-requests
   */
  async openDocumentCorrectionOpsInfoRequest(input: {
    requestId: string;
    safeExplanationText: string;
    clientIdempotencyKey: string;
  }): Promise<DocumentCorrectionOpsOpenInfoRequestResponseDto> {
    const response = await this.request<
      ApiResponse<DocumentCorrectionOpsOpenInfoRequestResponseDto>
    >(
      `/api/workspace/portal/document-correction-requests/${encodeURIComponent(input.requestId)}/info-requests`,
      {
        method: 'POST',
        body: JSON.stringify({
          safeExplanationText: input.safeExplanationText,
          clientIdempotencyKey: input.clientIdempotencyKey,
        }),
      }
    );
    if (!response.data) {
      throw new Error(DOCUMENT_CORRECTION_OPS_INFO_REQUEST_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/portal/document-correction-requests/:requestId/review-context
   */
  async getDocumentCorrectionOpsReviewContext(
    requestId: string
  ): Promise<DocumentCorrectionOpsReviewContextDto> {
    const response = await this.request<ApiResponse<DocumentCorrectionOpsReviewContextDto>>(
      `/api/workspace/portal/document-correction-requests/${encodeURIComponent(requestId)}/review-context`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_OPERATOR_LABELS.loadContextFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/portal/document-correction-requests/:requestId/review-decision
   */
  async recordDocumentCorrectionOpsReviewDecision(input: {
    requestId: string;
    decision: DocumentCorrectionReviewDecisionWire;
    reasonCode: DocumentCorrectionReviewDecisionReasonCode;
    safeExplanationText: string;
    sourceVersion: string;
    clientIdempotencyKey: string;
  }): Promise<DocumentCorrectionOpsReviewDecisionResponseDto> {
    const response = await this.request<
      ApiResponse<DocumentCorrectionOpsReviewDecisionResponseDto>
    >(
      `/api/workspace/portal/document-correction-requests/${encodeURIComponent(input.requestId)}/review-decision`,
      {
        method: 'POST',
        body: JSON.stringify({
          decision: input.decision,
          reasonCode: input.reasonCode,
          safeExplanationText: input.safeExplanationText,
          sourceVersion: input.sourceVersion,
          clientIdempotencyKey: input.clientIdempotencyKey,
        }),
      }
    );
    if (!response.data) {
      throw new Error(DOCUMENT_CORRECTION_OPS_REVIEW_DECISION_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/credit-holds?category=&limit=&offset=
   * Enforcement CreditHold manager queue (not advisory recommendations).
   */
  async listCreditHoldQueue(params: {
    category: CreditHoldQueueCategory;
    limit?: number;
    offset?: number;
  }): Promise<CreditHoldQueuePage> {
    const path = appendFinanceListQueryParams('/api/workspace/credit-holds', {
      category: params.category,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<CreditHoldQueuePage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load credit hold queue');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/final-request-documents?category=&limit=&offset=
   * Enforcement Final Request document manager queue (READ_ONLY; not prep recommendations).
   */
  async listFinalRequestDocumentQueue(params: {
    category: FinalRequestDocumentQueueCategory;
    limit?: number;
    offset?: number;
  }): Promise<FinalRequestDocumentQueuePage> {
    const path = appendFinanceListQueryParams('/api/workspace/final-request-documents', {
      category: params.category,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<FinalRequestDocumentQueuePage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load final request document queue');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/final-request-documents/:finalRequestDocumentId
   * Existing detail DTO (may include recipientProfile — no new masking).
   */
  async getFinalRequestDocument(finalRequestDocumentId: string): Promise<{
    document: Record<string, unknown>;
  }> {
    const response = await this.request<ApiResponse<{ document: Record<string, unknown> }>>(
      `/api/workspace/final-request-documents/${encodeURIComponent(finalRequestDocumentId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error('Failed to load final request document');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/legal-action-requests?category=&limit=&offset=
   * Enforcement Legal Action Request manager queue (READ_ONLY).
   */
  async listLegalActionRequestQueue(params: {
    category: LegalActionRequestQueueCategory;
    limit?: number;
    offset?: number;
  }): Promise<LegalActionRequestQueuePage> {
    const path = appendFinanceListQueryParams('/api/workspace/legal-action-requests', {
      category: params.category,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<LegalActionRequestQueuePage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load legal action request queue');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/legal-action-requests/:legalActionRequestId
   * Existing detail DTO (may include notes/handoffPackage — UI must not dump free text).
   */
  async getLegalActionRequest(legalActionRequestId: string): Promise<{
    document: Record<string, unknown>;
  }> {
    const response = await this.request<ApiResponse<{ document: Record<string, unknown> }>>(
      `/api/workspace/legal-action-requests/${encodeURIComponent(legalActionRequestId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error('Failed to load legal action request');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/settlement-requests?category=&limit=&offset=
   * Enforcement Settlement Request manager queue (READ_ONLY).
   */
  async listCollectionSettlementRequestQueue(params: {
    category: CollectionSettlementRequestQueueCategory;
    limit?: number;
    offset?: number;
  }): Promise<CollectionSettlementRequestQueuePage> {
    const path = appendFinanceListQueryParams('/api/workspace/settlement-requests', {
      category: params.category,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<CollectionSettlementRequestQueuePage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load settlement request queue');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/settlement-requests/:settlementRequestId
   * Existing detail DTO (may include notes — UI must not dump free text).
   */
  async getCollectionSettlementRequest(settlementRequestId: string): Promise<{
    document: Record<string, unknown>;
  }> {
    const response = await this.request<ApiResponse<{ document: Record<string, unknown> }>>(
      `/api/workspace/settlement-requests/${encodeURIComponent(settlementRequestId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error('Failed to load settlement request');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/accounting-posting-requests?category=&limit=&offset=
   * Enforcement Accounting APR manager queue (READ_ONLY).
   */
  async listAccountingPostingRequestQueue(params: {
    category: AccountingPostingRequestQueueCategory;
    limit?: number;
    offset?: number;
  }): Promise<AccountingPostingRequestQueuePage> {
    const path = appendFinanceListQueryParams('/api/workspace/accounting-posting-requests', {
      category: params.category,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<AccountingPostingRequestQueuePage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load accounting posting request queue');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/accounting-posting-requests/:id
   * Existing detail DTO (may include notes/failureMessage — UI must not dump free text).
   */
  async getAccountingPostingRequest(
    accountingPostingRequestId: string
  ): Promise<Record<string, unknown>> {
    const response = await this.request<ApiResponse<Record<string, unknown>>>(
      `/api/workspace/accounting-posting-requests/${encodeURIComponent(accountingPostingRequestId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error('Failed to load accounting posting request');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/credit-holds/:creditHoldId/approve
   */
  async approveCreditHold(
    creditHoldId: string,
    payload: { expectedVersion: number; reason?: string | null; requestIdentity?: string | null }
  ): Promise<{ hold: unknown; outcome: string }> {
    const response = await this.request<
      ApiResponse<{ hold: unknown; supersededHold: unknown; outcome: string }>
    >(`/api/workspace/credit-holds/${encodeURIComponent(creditHoldId)}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error('Failed to approve credit hold');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/credit-holds/:creditHoldId/release
   */
  async releaseCreditHold(
    creditHoldId: string,
    payload: {
      expectedVersion: number;
      releaseReason: string;
      requestIdentity?: string | null;
    }
  ): Promise<{ hold: unknown; outcome: string }> {
    const response = await this.request<ApiResponse<{ hold: unknown; outcome: string }>>(
      `/api/workspace/credit-holds/${encodeURIComponent(creditHoldId)}/release`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error('Failed to release credit hold');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/order-gate/denials?limit=&offset=
   * Tenant-scoped blocked Order Gate evaluations (default last 7 days).
   */
  async listOrderGateDenials(params?: {
    limit?: number;
    offset?: number;
  }): Promise<OrderGateDenialLogPage> {
    const path = appendFinanceListQueryParams('/api/workspace/order-gate/denials', {
      ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      ...(params?.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<OrderGateDenialLogPage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load order gate denials');
    }
    return response.data;
  }

  /**
   * GET /api/workspace/order-gate/overrides?scope=active|historical&limit=&offset=
   * Server-resolved override lifecycle lists (do not recompute status from expiresAt).
   */
  async listOrderGateOverrides(params: {
    scope: OrderGateOverrideListScope;
    limit?: number;
    offset?: number;
  }): Promise<OrderGateOverrideListPage> {
    const path = appendFinanceListQueryParams('/api/workspace/order-gate/overrides', {
      scope: params.scope,
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
      ...(params.offset !== undefined ? { offset: params.offset } : {}),
    });
    const response = await this.request<ApiResponse<OrderGateOverrideListPage>>(path, {
      method: 'GET',
    });
    if (!response.data) {
      throw new Error('Failed to load order gate overrides');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/external-entities/:externalEntityId/order-gate/overrides
   */
  async createOrderGateOverride(
    externalEntityId: string,
    payload: {
      reason: string;
      expiresAt: string;
      clientIdempotencyKey?: string | null;
    }
  ): Promise<{ outcome: string; override: { id: string; version: number } }> {
    const response = await this.request<
      ApiResponse<{ outcome: string; override: { id: string; version: number } }>
    >(
      `/api/workspace/external-entities/${encodeURIComponent(externalEntityId)}/order-gate/overrides`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error('Failed to create order gate override');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/order-gate/overrides/:overrideId/revoke
   */
  async revokeOrderGateOverride(
    overrideId: string,
    payload: { expectedVersion: number }
  ): Promise<{ outcome: string; override: { id: string; version: number } }> {
    const response = await this.request<
      ApiResponse<{ outcome: string; override: { id: string; version: number } }>
    >(`/api/workspace/order-gate/overrides/${encodeURIComponent(overrideId)}/revoke`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error('Failed to revoke order gate override');
    }
    return response.data;
  }

  /**
   * POST /api/workspace/external-entities/:externalEntityId/credit-hold-recommendations/:recommendationId/dismiss
   */
  async dismissCreditHoldRecommendation(
    externalEntityId: string,
    recommendationId: string,
    payload?: { dismissalReasonCode?: string }
  ): Promise<CollectionCreditHoldDismissResponseDto> {
    const response = await this.request<ApiResponse<CollectionCreditHoldDismissResponseDto>>(
      `/api/workspace/external-entities/${encodeURIComponent(externalEntityId)}/credit-hold-recommendations/${encodeURIComponent(recommendationId)}/dismiss`,
      {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/external-entities/:externalEntityId/credit-hold-recommendations/:recommendationId/defer
   */
  async deferCreditHoldRecommendation(
    externalEntityId: string,
    recommendationId: string,
    payload: {
      sourceFingerprint: string;
      clientIdempotencyKey: string;
      reasonCode: string;
      expiresAt: string;
      notes?: string | null;
    }
  ): Promise<CollectionCreditHoldDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionCreditHoldDispositionMutationResponseDto>
    >(
      `/api/workspace/external-entities/${encodeURIComponent(externalEntityId)}/credit-hold-recommendations/${encodeURIComponent(recommendationId)}/defer`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/external-entities/:externalEntityId/credit-hold-recommendations/:recommendationId/cancel-defer
   */
  async cancelCreditHoldRecommendationDefer(
    externalEntityId: string,
    recommendationId: string,
    payload: {
      sourceFingerprint: string;
      clientIdempotencyKey: string;
      notes?: string | null;
      targetDispositionId?: string | null;
    }
  ): Promise<CollectionCreditHoldDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionCreditHoldDispositionMutationResponseDto>
    >(
      `/api/workspace/external-entities/${encodeURIComponent(externalEntityId)}/credit-hold-recommendations/${encodeURIComponent(recommendationId)}/cancel-defer`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/external-entities/:externalEntityId/credit-hold-recommendations/:recommendationId/escalate-to-credit-review
   */
  async escalateCreditHoldRecommendationToCreditReview(
    externalEntityId: string,
    recommendationId: string,
    payload: {
      sourceFingerprint: string;
      clientIdempotencyKey: string;
      notes?: string | null;
    }
  ): Promise<CollectionCreditHoldDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionCreditHoldDispositionMutationResponseDto>
    >(
      `/api/workspace/external-entities/${encodeURIComponent(externalEntityId)}/credit-hold-recommendations/${encodeURIComponent(recommendationId)}/escalate-to-credit-review`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_HOLD_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/collection/final-request-prep-recommendations
   */
  async listFinalRequestPrepRecommendations(params?: {
    limit?: number;
    offset?: number;
    includeDeferred?: boolean;
  }): Promise<CollectionFinalRequestPrepRecommendationQueuePage> {
    const path = appendFinanceListQueryParams(
      '/api/workspace/collection/final-request-prep-recommendations',
      {
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.offset !== undefined ? { offset: params.offset } : {}),
        ...(params?.includeDeferred ? { includeDeferred: 'true' } : {}),
      }
    );
    const response = await this.request<
      ApiResponse<CollectionFinalRequestPrepRecommendationQueuePage>
    >(path, { method: 'GET' });
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/final-request-prep-recommendations/active
   */
  async getActiveFinalRequestPrepRecommendations(
    arEntryId: string,
    params?: { prepRecommendationCode?: string }
  ): Promise<CollectionFinalRequestPrepRecommendationActivePage> {
    const search = new URLSearchParams();
    if (params?.prepRecommendationCode) {
      search.set('prepRecommendationCode', params.prepRecommendationCode);
    }
    const query = search.toString();
    const response = await this.request<
      ApiResponse<CollectionFinalRequestPrepRecommendationActivePage>
    >(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/final-request-prep-recommendations/active${query ? `?${query}` : ''}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/final-request-prep-recommendations/:recommendationId/dismiss
   */
  async dismissFinalRequestPrepRecommendation(
    arEntryId: string,
    recommendationId: string,
    payload?: { dismissalReasonCode?: string }
  ): Promise<CollectionFinalRequestPrepDismissResponseDto> {
    const response = await this.request<ApiResponse<CollectionFinalRequestPrepDismissResponseDto>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/final-request-prep-recommendations/${encodeURIComponent(recommendationId)}/dismiss`,
      { method: 'POST', body: JSON.stringify(payload ?? {}) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/final-request-prep-recommendations/:recommendationId/defer-prep
   */
  async deferFinalRequestPrepRecommendation(
    arEntryId: string,
    recommendationId: string,
    payload: {
      sourceSnapshotHash: string;
      clientIdempotencyKey: string;
      deferReasonCode: string;
      deferUntil: string;
      note?: string | null;
    }
  ): Promise<CollectionFinalRequestPrepDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionFinalRequestPrepDispositionMutationResponseDto>
    >(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/final-request-prep-recommendations/${encodeURIComponent(recommendationId)}/defer-prep`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/final-request-prep-recommendations/:recommendationId/cancel-defer
   */
  async cancelDeferFinalRequestPrepRecommendation(
    arEntryId: string,
    recommendationId: string,
    payload: {
      sourceSnapshotHash: string;
      clientIdempotencyKey: string;
      targetDispositionId?: string | null;
      note?: string | null;
    }
  ): Promise<CollectionFinalRequestPrepDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionFinalRequestPrepDispositionMutationResponseDto>
    >(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/final-request-prep-recommendations/${encodeURIComponent(recommendationId)}/cancel-defer`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/final-request-prep-recommendations/:recommendationId/approve-prep
   */
  async approveFinalRequestPrepRecommendation(
    arEntryId: string,
    recommendationId: string,
    payload: {
      sourceSnapshotHash: string;
      clientIdempotencyKey: string;
      note?: string | null;
    }
  ): Promise<CollectionFinalRequestPrepDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionFinalRequestPrepDispositionMutationResponseDto>
    >(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/final-request-prep-recommendations/${encodeURIComponent(recommendationId)}/approve-prep`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_FINAL_REQUEST_PREP_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/collection-credit-review-requests
   */
  async listCollectionCreditReviewRequests(params?: {
    includeDeferred?: boolean;
    externalEntityId?: string;
    limit?: number;
    offset?: number;
  }): Promise<CollectionCreditReviewRequestQueuePage> {
    const search = new URLSearchParams();
    if (params?.includeDeferred) {
      search.set('includeDeferred', 'true');
    }
    if (params?.externalEntityId) {
      search.set('externalEntityId', params.externalEntityId);
    }
    if (params?.limit != null) {
      search.set('limit', String(params.limit));
    }
    if (params?.offset != null) {
      search.set('offset', String(params.offset));
    }
    const query = search.toString();
    const response = await this.request<ApiResponse<CollectionCreditReviewRequestQueuePage>>(
      `/api/workspace/collection-credit-review-requests${query ? `?${query}` : ''}`
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS.loadFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/collection-credit-review-requests/:requestId/claim
   */
  async claimCollectionCreditReviewRequest(
    requestId: string,
    payload: { expectedVersion: number }
  ): Promise<{ request: CollectionCreditReviewRequestDto }> {
    const response = await this.request<ApiResponse<{ request: CollectionCreditReviewRequestDto }>>(
      `/api/workspace/collection-credit-review-requests/${encodeURIComponent(requestId)}/claim`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/collection-credit-review-requests/:requestId/resolve
   */
  async resolveCollectionCreditReviewRequest(
    requestId: string,
    payload: {
      expectedVersion: number;
      resolutionCode: string;
      resolutionNote?: string | null;
    }
  ): Promise<{ request: CollectionCreditReviewRequestDto }> {
    const response = await this.request<ApiResponse<{ request: CollectionCreditReviewRequestDto }>>(
      `/api/workspace/collection-credit-review-requests/${encodeURIComponent(requestId)}/resolve`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/collection-credit-review-requests/:requestId/defer
   */
  async deferCollectionCreditReviewRequest(
    requestId: string,
    payload: { expectedVersion: number; deferredUntil: string }
  ): Promise<{ request: CollectionCreditReviewRequestDto }> {
    const response = await this.request<ApiResponse<{ request: CollectionCreditReviewRequestDto }>>(
      `/api/workspace/collection-credit-review-requests/${encodeURIComponent(requestId)}/defer`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/collection-credit-review-requests/:requestId/cancel
   */
  async cancelCollectionCreditReviewRequest(
    requestId: string,
    payload: { expectedVersion: number }
  ): Promise<{ request: CollectionCreditReviewRequestDto }> {
    const response = await this.request<ApiResponse<{ request: CollectionCreditReviewRequestDto }>>(
      `/api/workspace/collection-credit-review-requests/${encodeURIComponent(requestId)}/cancel`,
      { method: 'POST', body: JSON.stringify(payload) }
    );
    if (!response.data) {
      throw new Error(COLLECTION_CREDIT_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/manager-review-dispositions/cancel-defer
   */
  async cancelManagerReviewDefer(payload: {
    collectionCaseId: string;
    exceptionCode: string;
    sourceFingerprint: string;
    clientIdempotencyKey: string;
    notes?: string | null;
    targetDispositionId?: string | null;
  }): Promise<CollectionManagerReviewDispositionMutationResponseDto> {
    const response = await this.request<
      ApiResponse<CollectionManagerReviewDispositionMutationResponseDto>
    >('/api/workspace/ar-collections/manager-review-dispositions/cancel-defer', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.data) {
      throw new Error(COLLECTION_MANAGER_REVIEW_OPERATOR_LABELS.mutationFailed);
    }
    return response.data;
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/recommendations/:recommendationId/dismiss
   */
  async dismissArCollectionRecommendation(
    arEntryId: string,
    recommendationId: string,
    payload?: { dismissalReasonCode?: string }
  ): Promise<{
    id: string;
    arEntryId: string;
    collectionCaseId: string;
    recommendationKind: string;
    recommendedStage: string;
    status: string;
    dismissedAt: string | null;
    dismissedByActorId: string | null;
    dismissalReasonCode: string | null;
    idempotent: boolean;
  }> {
    const response = await this.request<
      ApiResponse<{
        id: string;
        arEntryId: string;
        collectionCaseId: string;
        recommendationKind: string;
        recommendedStage: string;
        status: string;
        dismissedAt: string | null;
        dismissedByActorId: string | null;
        dismissalReasonCode: string | null;
        idempotent: boolean;
      }>
    >(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/recommendations/${encodeURIComponent(recommendationId)}/dismiss`,
      {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_STAGE_RECOMMENDATION_OPERATOR_LABELS.dismissFailed);
    }
    return response.data;
  }

  /**
   * GET /api/workspace/ar-collections/:arEntryId/commitments
   */
  async listArCollectionCommitments(arEntryId: string): Promise<CollectionCommitment[]> {
    const response = await this.request<ApiResponse<CollectionCommitment[]>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/commitments`,
      { method: 'GET' }
    );
    return response.data ?? [];
  }

  /**
   * POST /api/workspace/ar-collections/:arEntryId/commitments
   */
  async createArCollectionCommitment(
    arEntryId: string,
    payload: {
      promisedAmount?: string;
      promisedDate?: string;
      notes?: string;
      collectionStage?: string;
    }
  ): Promise<CollectionCommitment> {
    const response = await this.request<ApiResponse<CollectionCommitment>>(
      `/api/workspace/ar-collections/${encodeURIComponent(arEntryId)}/commitments`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_COMMITMENT_OPERATOR_LABELS.createFailed);
    }
    return response.data;
  }

  /**
   * PATCH /api/workspace/ar-commitments/:commitmentId — cancel pending only (V0).
   */
  async cancelArCollectionCommitment(commitmentId: string): Promise<CollectionCommitment> {
    const response = await this.request<ApiResponse<CollectionCommitment>>(
      `/api/workspace/ar-commitments/${encodeURIComponent(commitmentId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status: 'cancelled' }),
      }
    );
    if (!response.data) {
      throw new Error(COLLECTION_COMMITMENT_OPERATOR_LABELS.cancelFailed);
    }
    return response.data;
  }

  async getUnifiedDashboard(options?: {
    days?: number;
    includeAI?: boolean;
    domains?: string[];
  }): Promise<{
    dashboardId: string;
    unifiedMetrics: {
      totalBusinessValue: number;
      totalTransactions: number;
      systemHealthScore: number;
      growthRate: number;
      efficiencyScore: number;
    };
    domainSummaries: Record<string, unknown>;
    recentActivity: Array<{
      activityId: string;
      activityType: string;
      domain: string;
      objectName: string;
      timestamp: string;
      impact: string;
      description: string;
    }>;
    aiInsights?: unknown;
  }> {
    const params = new URLSearchParams();
    if (options?.days !== undefined) params.set('days', String(options.days));
    if (options?.includeAI) params.set('includeAI', 'true');
    if (options?.domains?.length) params.set('domains', options.domains.join(','));
    const qs = params.toString();
    const response = await this.request<{
      success: boolean;
      data?: {
        dashboardId: string;
        unifiedMetrics: {
          totalBusinessValue: number;
          totalTransactions: number;
          systemHealthScore: number;
          growthRate: number;
          efficiencyScore: number;
        };
        domainSummaries: Record<string, unknown>;
        recentActivity: Array<{
          activityId: string;
          activityType: string;
          domain: string;
          objectName: string;
          timestamp: string;
          impact: string;
          description: string;
        }>;
        aiInsights?: unknown;
      };
    }>(`/api/integration/dashboard${qs ? `?${qs}` : ''}`);

    if (response.data) {
      return response.data;
    }
    return {
      dashboardId: '',
      unifiedMetrics: {
        totalBusinessValue: 0,
        totalTransactions: 0,
        systemHealthScore: 0,
        growthRate: 0,
        efficiencyScore: 0,
      },
      domainSummaries: {},
      recentActivity: [],
    };
  }

  async searchGlobal(query: string, limit = 20): Promise<GlobalSearchResult> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const response = await this.request<{
      success: boolean;
      data?: {
        items: GlobalSearchItem[];
        total: number;
        query: string;
      };
    }>(`/api/integration/search?${params.toString()}`);

    if (response.data) {
      return {
        items: response.data.items ?? [],
        total: response.data.total ?? 0,
        query: response.data.query ?? query,
      };
    }

    return { items: [], total: 0, query };
  }

  /**
   * baseUrl getter (필요시 사용)
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * 스키마 기반 Flow 구성 제안
   * GET /api/flow/suggestions/from-schema?businessDomain=...
   * POST /api/flow/suggestions/from-schema { businessDomain, previousSchema? }
   */
  async getFlowSuggestionsFromSchema(
    businessDomain: string,
    options?: { previousSchema?: DomainFieldSchema }
  ): Promise<
    Array<{
      ruleId: string;
      equation: string;
      reason: string;
      severity: 'error' | 'warn';
      createFlowDeepLink?: string;
      openFlowAction?: 'navigate' | 'v2_open';
      openFlowDefinitionId?: string;
      openFlowParams?: Record<string, unknown>;
      counterpartSuggestion?: {
        reason: string;
        createFlowDeepLink?: string;
        openFlowAction?: 'navigate' | 'v2_open';
        openFlowDefinitionId?: string;
      };
    }>
  > {
    type FlowSuggestionResponse = Array<{
      ruleId: string;
      equation: string;
      reason: string;
      severity: 'error' | 'warn';
      createFlowDeepLink?: string;
      openFlowAction?: 'navigate' | 'v2_open';
      openFlowDefinitionId?: string;
      openFlowParams?: Record<string, unknown>;
      counterpartSuggestion?: {
        reason: string;
        createFlowDeepLink?: string;
        openFlowAction?: 'navigate' | 'v2_open';
        openFlowDefinitionId?: string;
      };
    }>;

    if (options?.previousSchema) {
      const response = await this.request<ApiResponse<FlowSuggestionResponse>>(
        '/api/flow/suggestions/from-schema',
        {
          method: 'POST',
          body: JSON.stringify({
            businessDomain,
            previousSchema: options.previousSchema,
          }),
        }
      );
      return response.data ?? [];
    }

    const params = new URLSearchParams({ businessDomain });
    const response = await this.request<ApiResponse<FlowSuggestionResponse>>(
      `/api/flow/suggestions/from-schema?${params.toString()}`
    );
    return response.data ?? [];
  }

  /**
   * Generic v2 Flow execute — POST /api/flow/v2/execute
   */
  async executeV2Flow(input: {
    flowDefinitionId: string;
    executedBy?: string;
    [key: string]: unknown;
  }): Promise<{
    flowExecution: unknown;
    updatedSchema?: DomainFieldSchema;
    updatedEntity?: unknown;
  }> {
    const response = await this.request<
      ApiResponse<{
        flowExecution: unknown;
        updatedSchema?: DomainFieldSchema;
        updatedEntity?: unknown;
      }>
    >('/api/flow/v2/execute', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (!response.data) {
      throw new Error(`v2 Flow 실행 실패: ${input.flowDefinitionId}`);
    }
    return response.data;
  }

  /**
   * v2 AddDynamicField Flow 실행 — Dynamic Table 필드 추가 (Execution Model Phase 2)
   * POST /api/flow/v2/execute
   */
  async executeV2AddDynamicFieldFlow(input: {
    businessDomain: string;
    fieldArrayName: 'customFields' | 'valueFields' | 'impactFields' | 'relationFields';
    fieldDefinition: import('@shared/types/schema').AnyFieldDefinition;
    executedBy?: string;
  }): Promise<{ flowExecution: unknown; updatedSchema?: DomainFieldSchema }> {
    const response = await this.request<
      ApiResponse<{ flowExecution: unknown; updatedSchema?: DomainFieldSchema }>
    >('/api/flow/v2/execute', {
      method: 'POST',
      body: JSON.stringify({
        flowDefinitionId: 'v2.add_dynamic_field',
        ...input,
      }),
    });
    if (!response.data) {
      throw new Error(`AddDynamicField Flow 실행 실패: ${input.businessDomain}`);
    }
    return response.data;
  }

  /**
   * v2 FlowDefinition 상세 조회
   * GET /api/flow/v2/definitions/:flowDefinitionId
   */
  async getV2FlowDefinition(flowDefinitionId: string): Promise<V2FlowDefinitionSummary> {
    const response = await this.request<ApiResponse<V2FlowDefinitionSummary>>(
      `/api/flow/v2/definitions/${encodeURIComponent(flowDefinitionId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(`Flow 정의 조회 실패: ${flowDefinitionId}`);
    }
    return response.data;
  }

  /**
   * Flow policy 2FA — send email OTP
   * POST /api/flow/v2/2fa/send
   */
  async sendFlowTwoFactorCode(): Promise<FlowTwoFactorSendResult> {
    const response = await this.request<ApiResponse<FlowTwoFactorSendResult>>(
      '/api/flow/v2/2fa/send',
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
    if (!response.data) {
      throw new Error('2FA 인증 코드 발송에 실패했습니다.');
    }
    return response.data;
  }

  /**
   * v2 Flow 열기 — opened FlowExecution 반환
   * POST /api/flow/v2/open
   */
  async openV2Flow(input: {
    flowDefinitionId: string;
    input?: Record<string, unknown>;
    executedBy?: string;
    twoFactorCode?: string;
  }): Promise<V2FlowExecutionSummary> {
    const response = await this.request<ApiResponse<V2FlowExecutionSummary>>('/api/flow/v2/open', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (!response.data) {
      throw new Error(`Flow 열기 실패: ${input.flowDefinitionId}`);
    }
    return response.data;
  }

  /**
   * v2 FlowExecution 상세 조회
   * GET /api/flow/v2/executions/:executionId
   */
  async getV2FlowExecution(executionId: string): Promise<V2FlowExecutionSummary> {
    const response = await this.request<ApiResponse<V2FlowExecutionSummary>>(
      `/api/flow/v2/executions/${encodeURIComponent(executionId)}`,
      { method: 'GET' }
    );
    if (!response.data) {
      throw new Error(`FlowExecution 조회 실패: ${executionId}`);
    }
    return response.data;
  }

  /**
   * 내보내기 가능 서브도메인 목록
   * GET /api/export/subdomains
   */
  async fetchExportSubdomains(): Promise<ExportSubdomainInfo[]> {
    const response =
      await this.request<ApiResponse<ExportSubdomainsResponse>>('/api/export/subdomains');
    return response.data?.subdomains ?? [];
  }

  /**
   * 테넌트 데이터 내보내기 (브라우저 다운로드)
   * GET /api/export/:subdomain?format=csv|excel&tenantId=...
   */
  async exportData(subdomain: string, format: 'csv' | 'excel' = 'csv'): Promise<void> {
    const params = new URLSearchParams({
      format,
      tenantId: this.tenantId,
    });
    const url = `${this.baseUrl}/api/export/${encodeURIComponent(subdomain)}?${params.toString()}`;

    const response = await fetchWithAuth(url, {
      headers: {
        Accept: 'application/octet-stream, text/csv, application/json',
        'x-tenant-id': this.tenantId,
        ...getAuthHeaders(),
      },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let message = `내보내기 실패: ${response.status}`;
      try {
        const parsed = JSON.parse(errorText) as ApiErrorBody;
        message = parsed.message || parsed.error || message;
      } catch {
        if (errorText) {
          message = errorText;
        }
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const extension = format === 'excel' ? 'xlsx' : 'csv';
    const filename = `${subdomain}-${new Date().toISOString().slice(0, 10)}.${extension}`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  /**
   * Export row estimate — sync vs async routing
   * GET /api/export/estimate/:subdomain
   */
  async estimateExportRows(subdomain: string): Promise<ExportEstimateResult> {
    const params = new URLSearchParams({ tenantId: this.tenantId });
    const response = await this.request<ApiResponse<ExportEstimateResult>>(
      `/api/export/estimate/${encodeURIComponent(subdomain)}?${params.toString()}`
    );
    if (!response.data) {
      throw new Error(`Export estimate failed: ${subdomain}`);
    }
    return response.data;
  }

  /**
   * Create async export job
   * POST /api/export/jobs
   */
  async createExportJob(
    subdomain: string,
    format: 'csv' | 'excel' = 'csv'
  ): Promise<ExportJobView> {
    const response = await this.request<ApiResponse<ExportJobView>>('/api/export/jobs', {
      method: 'POST',
      body: JSON.stringify({ subdomain, format }),
    });
    if (!response.data) {
      throw new Error('Export job creation failed');
    }
    return response.data;
  }

  /**
   * Fetch export job status
   * GET /api/export/jobs/:jobId
   */
  async fetchExportJob(jobId: string): Promise<ExportJobView> {
    const params = new URLSearchParams({ tenantId: this.tenantId });
    const response = await this.request<ApiResponse<ExportJobView>>(
      `/api/export/jobs/${encodeURIComponent(jobId)}?${params.toString()}`
    );
    if (!response.data) {
      throw new Error(`Export job not found: ${jobId}`);
    }
    return response.data;
  }

  /**
   * List recent export jobs
   * GET /api/export/jobs
   */
  async listExportJobs(): Promise<ExportJobView[]> {
    const params = new URLSearchParams({ tenantId: this.tenantId });
    const response = await this.request<ApiResponse<{ jobs: ExportJobView[] }>>(
      `/api/export/jobs?${params.toString()}`
    );
    return response.data?.jobs ?? [];
  }

  /**
   * Download completed export job file
   * GET /api/export/jobs/:jobId/download
   */
  async downloadExportJob(job: ExportJobView): Promise<void> {
    if (!job.downloadUrl) {
      throw new Error('Export download URL is not available');
    }

    const params = new URLSearchParams({ tenantId: this.tenantId });
    const url = `${this.baseUrl}${job.downloadUrl}?${params.toString()}`;

    const response = await fetchWithAuth(url, {
      headers: {
        Accept: 'application/octet-stream, text/csv, application/json',
        'x-tenant-id': this.tenantId,
        ...getAuthHeaders(),
      },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let message = `Export download failed: ${response.status}`;
      try {
        const parsed = JSON.parse(errorText) as ApiErrorBody;
        message = parsed.message || parsed.error || message;
      } catch {
        if (errorText) {
          message = errorText;
        }
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const extension = job.format === 'excel' ? 'xlsx' : 'csv';
    const filename = `${job.subdomain}-${new Date().toISOString().slice(0, 10)}.${extension}`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  /**
   * 스키마 업데이트
   * PUT /api/system/schema/:subDomain
   */
  async updateSchema(
    businessDomain: string,
    schemaData: Partial<DomainFieldSchema>
  ): Promise<DomainFieldSchema> {
    const response = await this.request<ApiResponse<DomainFieldSchema>>(
      `/api/system/schema/${businessDomain}`,
      {
        method: 'PUT',
        body: JSON.stringify(schemaData),
      }
    );
    if (!response.data) {
      throw new Error(`Schema update failed for ${businessDomain}`);
    }
    return response.data;
  }

  /**
   * 관계 목록 조회
   * GET /api/system/relations
   */
  async getRelations(filters?: {
    sourceObjectId?: string;
    targetObjectId?: string;
    sourceBusinessDomain?: string;
    targetBusinessDomain?: string;
  }): Promise<Record<string, unknown>[]> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const url = queryParams.toString()
      ? `/api/system/relations?${queryParams.toString()}`
      : '/api/system/relations';
    const response = await this.request<ApiListResponse<Record<string, unknown>>>(url);
    return response.data ?? [];
  }

  /**
   * 관계 네트워크 조회 (역방향)
   * GET /api/system/relations/network?targetObjectId=xxx
   */
  async getRelationNetwork(targetObjectId: string): Promise<RelationNetworkNode[]> {
    const response = await this.request<ApiListResponse<RelationNetworkNode>>(
      `/api/system/relations/network?targetObjectId=${targetObjectId}`
    );
    return response.data || [];
  }
}

// ============================================
// 🔄 Factory 함수
// ============================================

/**
 * BackendApiClient 인스턴스 생성
 */
export function createBackendApiClient(tenantId: string, baseUrl?: string): BackendApiClient {
  return new BackendApiClient(tenantId, baseUrl);
}
